import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function CaliforniaToTexasRoutePage() {
  // Dynamically manage SEO browser titles & meta descriptions
  useEffect(() => {
  document.title = "Tesla Shipping Routes USA | State-to-State EV Transport";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Explore popular Tesla shipping routes across the USA, including California to Texas, California to Florida, Hawaii transport options, and nationwide EV shipping services.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Ship Tesla California to Texas, Ship Tesla from California to Florida, Cross-country Tesla transport USA, Tesla shipping routes USA, Interstate EV transport services, Tesla car shipping routes USA, Tesla transport California to Texas, Tesla transport California to Florida, Coast-to-coast Tesla shipping, Tesla shipping to Hawaii costs, Long-distance EV transport, Door-to-door Tesla shipping USA, State-to-state Tesla transport, Geo-targeted vehicle transport service, Electric vehicle shipping nationwide'
  );
}, []);

  const faqs = [
    {
      question: "How long does it take to ship a Tesla from California to Texas?",
      answer: "Typically 3--5 days door to door, plus a short pickup window."
    },
    {
      question: "How much does it cost?",
      answer: "Roughly $1,350--$1,500 open or $2,100--$2,400 enclosed; get an exact quote for your cities in 60 seconds."
    }
  ];

  // Combined Graph Schema (Service Route + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Tesla Shipping California to Texas",
        "areaServed": [
          { "@type": "State", "name": "California" },
          { "@type": "State", "name": "Texas" }
        ],
        "serviceType": "Vehicle Transport"
      },
      {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much does it cost to ship a Tesla from California to Texas?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Shipping typically ranges from $1,350 to $1,500 for open transport and $2,100 to $2,400 for enclosed carriers, depending on distance, timing, and vehicle specifications."
      }
    },
    {
      "@type": "Question",
      "name": "How long does it take to ship a Tesla from California to Texas?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most shipments between California and Texas are completed within 3 to 5 days using door-to-door transport services."
      }
    },
    {
      "@type": "Question",
      "name": "Is California to Texas a high-availability shipping route?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, this is one of the busiest auto transport routes in the United States, offering strong carrier availability and competitive pricing."
      }
    },
    {
      "@type": "Question",
      "name": "How is a Tesla secured during transport?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tesla vehicles are secured using EV-safe soft-strap systems, with proper loading techniques and battery charge management throughout transit."
      }
    },
    {
      "@type": "Question",
      "name": "Can I track my Tesla during shipping?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, real-time tracking and automated status updates are provided throughout the shipping process."
      }
    }
  ]
},
  {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Tesla Shipping from California to Texas",
  "description": "Professional Tesla vehicle shipping service from California to Texas using EV-certified carriers. Offers open and enclosed transport with real-time tracking, secure handling, and full insurance coverage.",
  "provider": {
    "@type": "Organization",
    "name": "ShipTesla",
    "url": "https://shiptesla.com"
  },
  "areaServed": [
    {
      "@type": "State",
      "name": "California"
    },
    {
      "@type": "State",
      "name": "Texas"
    }
  ],
  "serviceType": "Electric Vehicle Transport",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Tesla Shipping Options",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Open Carrier Tesla Transport"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Enclosed Carrier Tesla Transport"
        }
      }
    ]
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "Tesla Owners, Relocating Individuals, EV Buyers"
  }
}


    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Popular Routes</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="route-title">
              Ship a Tesla from California to Texas
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg border-l-2 border-accent pl-4 text-left italic my-6">
              Shipping a Tesla from California to Texas is a high-efficiency, high-availability route with competitive pricing and fast transit times. Costs typically range from $1,350–$1,500 (open) and $2,100–$2,400 (enclosed) across 1,200–1,400 miles, with delivery completed in 3–5 days.
            </p>
            <div className="mt-6">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Main Content Body */}
          <div className="space-y-8 text-muted-foreground md:text-lg leading-relaxed mb-12">
            <p>
              Whether you're relocating to Austin, establishing residency in a no-income-tax state, or taking delivery of a Tesla purchased in California, we handle the transport with EV-certified carriers and provide real-time updates from pickup through delivery.
            </p>
          </div>

          {/* Route-Specific FAQs */}
          <div className="mt-12 border-t border-border pt-12">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Guides Links Layout */}
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ship-a-tesla" className="text-accent hover:underline">How to Ship a Tesla</Link></li>
              <li><Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla Shipping Cost: 2026 Price Guide</Link></li>
              <li><Link to="/routes/california-to-florida" className="text-accent hover:underline">Ship a Tesla: California to Florida</Link></li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}


